export const COUNTRY_RISK_ORDER = { none: 0, standard: 1, medium: 2, high: 3 };

export const COUNTRY_RISK_IMPACT_OPTIONS = [
  { key: "none", label: "Not used" },
  { key: "medium", label: "Medium factor" },
  { key: "immediate_medium", label: "Immediate Medium" },
  { key: "high", label: "High factor" },
  { key: "immediate_high", label: "Immediate High" },
];

export const COUNTRY_RISK_SOURCE_RULE_OPTIONS = [
  { key: "fatfBlack", label: "FATF Black list" },
  { key: "fatfGrey", label: "FATF Grey list" },
  { key: "ofacPrograms", label: "OFAC sanctions" },
  { key: "taxHavens", label: "EU Tax havens" },
  { key: "euHighRisk", label: "EU High-Risk Third Countries" },
  { key: "cpiScore", label: "CPI score" },
  { key: "baselScore", label: "Basel AML Index" },
  { key: "baselScoreMedium", label: "Basel AML Index (medium range)" },
  { key: "baselScoreHigh", label: "Basel AML Index (high range)" },
];

export const COUNTRY_RISK_SOURCE_EVALUATION_ORDER = [
  "fatfBlack",
  "ofacPrograms",
  "fatfGrey",
  "taxHavens",
  "euHighRisk",
  "cpiScore",
  "baselScore",
  "baselScoreMedium",
  "baselScoreHigh",
];

export const DEFAULT_INDIVIDUAL_COMBINATION = {
  method: "weighted_worst",
  weightedOtherMethod: "equal_round_up",
  primaryCountry: "domicile",
};

export const UBO_COUNTRY_RISK_SCOPE_THRESHOLD_ONLY = "threshold_only";
export const UBO_COUNTRY_RISK_SCOPE_THRESHOLD_AND_DIRECT = "threshold_and_direct";
export const UBO_COUNTRY_RISK_SCOPE_ALL_UBOS = "all_ubos";
export const DEFAULT_UBO_COUNTRY_RISK_SCOPE = UBO_COUNTRY_RISK_SCOPE_THRESHOLD_ONLY;

export const DEFAULT_COUNTRY_RISK_RULES = {
  factors: {
    fatfBlack: { impact: "immediate_high" },
    ofacPrograms: { impact: "immediate_high" },
    fatfGrey: { impact: "medium" },
    taxHavens: { impact: "medium" },
    euHighRisk: { impact: "none" },
    cpiScore: { impact: "none" },
    baselScore: { impact: "none" },
    baselScoreMedium: { impact: "medium" },
    baselScoreHigh: { impact: "high" },
  },
  thresholds: {
    cpiScoreAtOrBelow: 40,
    baselScoreAtOrAbove: 6,
    mediumFactorsToMediumEnabled: true,
    mediumFactorsToMedium: 1,
    mediumFactorsToHighEnabled: false,
    mediumFactorsToHigh: 2,
    combinedFactorsToHigh: {
      enabled: false,
      highFactors: 1,
      mediumFactors: 2,
    },
    highFactorsToHighEnabled: true,
    highFactorsToHigh: 2,
  },
  individualCombination: DEFAULT_INDIVIDUAL_COMBINATION,
  entityUboScope: DEFAULT_UBO_COUNTRY_RISK_SCOPE,
};

export function normalizeCountryRiskImpact(value, fallback = "none") {
  const v = String(value || "").toLowerCase();
  if (v === "immediate_high" || v === "high" || v === "immediate_medium" || v === "medium" || v === "none") {
    return v;
  }
  if (v === "standard") return "none";
  return fallback;
}

export function sanitizeCountryRiskRules(raw) {
  const src = raw || {};
  const factors = {};
  COUNTRY_RISK_SOURCE_RULE_OPTIONS.forEach((source) => {
    const directImpact = src.factors?.[source.key]?.impact;
    const defaultImpact = DEFAULT_COUNTRY_RISK_RULES.factors[source.key]?.impact || "none";
    const impact = directImpact === undefined ? defaultImpact : directImpact;
    factors[source.key] = { impact: normalizeCountryRiskImpact(impact, defaultImpact) };
  });

  const srcThresholds = src.thresholds || {};
  const defaultThresholds = DEFAULT_COUNTRY_RISK_RULES.thresholds;
  const sourceCount = COUNTRY_RISK_SOURCE_RULE_OPTIONS.length;
  const cpiThresholdRaw = Number(srcThresholds.cpiScoreAtOrBelow);
  const cpiScoreAtOrBelow = Math.min(
    Math.max(
      0,
      Number.isFinite(cpiThresholdRaw) ? cpiThresholdRaw : defaultThresholds.cpiScoreAtOrBelow
    ),
    100
  );
  const baselThresholdRaw = Number(srcThresholds.baselScoreAtOrAbove);
  const baselScoreAtOrAbove = Math.min(
    Math.max(
      0,
      Number.isFinite(baselThresholdRaw) ? baselThresholdRaw : defaultThresholds.baselScoreAtOrAbove
    ),
    10
  );
  const mediumFactorsToHigh = Math.min(
    Math.max(1, Number(srcThresholds.mediumFactorsToHigh) || defaultThresholds.mediumFactorsToHigh),
    sourceCount
  );
  const mediumFactorsToMedium = Math.min(
    Math.max(1, Number(srcThresholds.mediumFactorsToMedium) || defaultThresholds.mediumFactorsToMedium),
    sourceCount
  );
  const highFactorsToHigh = Math.min(
    Math.max(1, Number(srcThresholds.highFactorsToHigh) || defaultThresholds.highFactorsToHigh),
    sourceCount
  );
  const combinedSource = srcThresholds.combinedFactorsToHigh || {};
  const combinedDefault = defaultThresholds.combinedFactorsToHigh;

  const individualCombination = normalizeIndividualCombination(src.individualCombination);
  const entityUboScope = normalizeUboCountryRiskScope(src.entityUboScope || src.uboCountryRiskScope);

  return {
    factors,
    individualCombination,
    entityUboScope,
    thresholds: {
      cpiScoreAtOrBelow,
      baselScoreAtOrAbove,
      mediumFactorsToMediumEnabled:
        srcThresholds.mediumFactorsToMediumEnabled === undefined
          ? defaultThresholds.mediumFactorsToMediumEnabled
          : srcThresholds.mediumFactorsToMediumEnabled === true,
      mediumFactorsToMedium,
      mediumFactorsToHighEnabled:
        srcThresholds.mediumFactorsToHighEnabled === undefined
          ? defaultThresholds.mediumFactorsToHighEnabled
          : srcThresholds.mediumFactorsToHighEnabled === true,
      mediumFactorsToHigh,
      combinedFactorsToHigh: {
        enabled: combinedSource.enabled === undefined ? combinedDefault.enabled : combinedSource.enabled === true,
        highFactors: Math.min(
          Math.max(1, Number(combinedSource.highFactors) || combinedDefault.highFactors),
          sourceCount
        ),
        mediumFactors: Math.min(
          Math.max(1, Number(combinedSource.mediumFactors) || combinedDefault.mediumFactors),
          sourceCount
        ),
      },
      highFactorsToHighEnabled:
        srcThresholds.highFactorsToHighEnabled === undefined
          ? defaultThresholds.highFactorsToHighEnabled
          : srcThresholds.highFactorsToHighEnabled === true,
      highFactorsToHigh,
    },
  };
}

export function normalizeUboCountryRiskScope(value) {
  const raw = String(value || "").trim();
  if (
    raw === UBO_COUNTRY_RISK_SCOPE_THRESHOLD_ONLY ||
    raw === UBO_COUNTRY_RISK_SCOPE_THRESHOLD_AND_DIRECT ||
    raw === UBO_COUNTRY_RISK_SCOPE_ALL_UBOS
  ) {
    return raw;
  }
  return DEFAULT_UBO_COUNTRY_RISK_SCOPE;
}

export function normalizeIndividualCombination(raw = {}) {
  const src = raw || {};
  const primaryCountry = ["nationality", "domicile"].includes(src.primaryCountry)
    ? src.primaryCountry
    : DEFAULT_INDIVIDUAL_COMBINATION.primaryCountry;
  const weightedOtherMethod = ["equal_round_up", "primary_raise"].includes(src.weightedOtherMethod)
    ? src.weightedOtherMethod
    : DEFAULT_INDIVIDUAL_COMBINATION.weightedOtherMethod;

  if (["weighted_worst", "domicile_only", "nationality_only", "weighted_others"].includes(src.method)) {
    return {
      method: src.method,
      weightedOtherMethod,
      primaryCountry,
    };
  }

  // Backward compatibility for previously saved settings.
  if (src.mode === "weighted") {
    if (src.secondaryCanRaise === false) {
      return {
        method: primaryCountry === "domicile" ? "domicile_only" : "nationality_only",
        weightedOtherMethod,
        primaryCountry,
      };
    }
    return {
      method: "weighted_others",
      weightedOtherMethod: "primary_raise",
      primaryCountry,
    };
  }

  if (src.mode === "max") {
    return {
      ...DEFAULT_INDIVIDUAL_COMBINATION,
      method: "weighted_worst",
    };
  }

  return { ...DEFAULT_INDIVIDUAL_COMBINATION };
}

export function countryRiskFromSourceKeys(rules, sourceKeys) {
  const cleanRules = sanitizeCountryRiskRules(rules);
  const impacts = (sourceKeys || []).map((key) =>
    normalizeCountryRiskImpact(cleanRules.factors?.[key]?.impact, "none")
  );
  const mediumCount = impacts.filter((impact) => impact === "medium").length;
  const highCount = impacts.filter((impact) => impact === "high").length;
  const mediumAndHighCount = mediumCount + highCount;
  const hasImmediateMedium = impacts.includes("immediate_medium");
  const hasImmediateHigh = impacts.includes("immediate_high");
  const thresholds = cleanRules.thresholds || {};
  const combined = thresholds.combinedFactorsToHigh || {};

  if (hasImmediateHigh) return "high";
  if (thresholds.highFactorsToHighEnabled && highCount >= (thresholds.highFactorsToHigh || 1)) return "high";
  if (thresholds.mediumFactorsToHighEnabled && mediumCount >= (thresholds.mediumFactorsToHigh || 1)) return "high";
  if (
    combined.enabled &&
    highCount >= (combined.highFactors || 1) &&
    mediumCount >= (combined.mediumFactors || 1)
  ) {
    return "high";
  }
  if (hasImmediateMedium) return "medium";
  if (
    thresholds.mediumFactorsToMediumEnabled &&
    mediumAndHighCount >= (thresholds.mediumFactorsToMedium || 1)
  ) {
    return "medium";
  }
  return "standard";
}

export function buildCountryRiskSourceKeys(iso3, sourceSets, { cpiScore, baselScore, rules } = {}) {
  if (!iso3 || !sourceSets) return [];
  const cleanRules = sanitizeCountryRiskRules(rules);
  return COUNTRY_RISK_SOURCE_EVALUATION_ORDER.filter((key) => {
    if (key === "cpiScore") {
      const score = Number(cpiScore);
      return Number.isFinite(score) && score <= cleanRules.thresholds.cpiScoreAtOrBelow;
    }
    if (key === "baselScore") {
      const score = Number(baselScore);
      return Number.isFinite(score) && score >= cleanRules.thresholds.baselScoreAtOrAbove;
    }
    if (key === "baselScoreMedium") {
      const score = Number(baselScore);
      return Number.isFinite(score) && score >= 5.5 && score <= 7;
    }
    if (key === "baselScoreHigh") {
      const score = Number(baselScore);
      return Number.isFinite(score) && score > 7;
    }
    return sourceSets[key]?.has(iso3);
  });
}

export function evaluateCountryRiskForIso3({ iso3, sourceSets, customSets, rules, cpiScore, baselScore }) {
  const hasIso = !!iso3;
  const sourceKeys = buildCountryRiskSourceKeys(iso3, sourceSets, { cpiScore, baselScore, rules });
  const defaultTier = sourceKeys.length ? countryRiskFromSourceKeys(rules, sourceKeys) : null;
  const customTier =
    hasIso && customSets?.high?.has(iso3)
      ? "high"
      : hasIso && customSets?.medium?.has(iso3)
      ? "medium"
      : hasIso && customSets?.standard?.has(iso3)
      ? "standard"
      : null;

  return {
    iso3,
    defaultSourceKeys: sourceKeys,
    defaultTier,
    customTier,
    finalTier: customTier || defaultTier || "standard",
    usedCustomOverride: !!customTier,
  };
}

export function combineGeoLevels(a, b, combination = {}) {
  const cleanCombination = normalizeIndividualCombination(combination);
  const order = { low: 1, medium: 2, high: 3 };
  const na = a && order[a] ? order[a] : 0;
  const nb = b && order[b] ? order[b] : 0;
  const toLevel = (n) => (n === 3 ? "high" : n === 2 ? "medium" : n === 1 ? "low" : null);

  if (cleanCombination.method === "domicile_only") {
    return nb ? toLevel(nb) : na ? toLevel(na) : null;
  }

  if (cleanCombination.method === "nationality_only") {
    return na ? toLevel(na) : nb ? toLevel(nb) : null;
  }

  if (cleanCombination.method === "weighted_others") {
    if (!na && !nb) return null;
    if (!na) return toLevel(nb);
    if (!nb) return toLevel(na);

    if (cleanCombination.weightedOtherMethod === "equal_round_up") {
      return toLevel(Math.ceil((na + nb) / 2));
    }

    const pn = cleanCombination.primaryCountry === "domicile" ? nb : na;
    const sn = cleanCombination.primaryCountry === "domicile" ? na : nb;
    if (!pn && !sn) return null;
    if (!pn) return toLevel(sn);
    const result = sn > pn ? Math.min(3, pn + 1) : pn;
    return toLevel(result);
  }

  // "weighted_worst" (default)
  return toLevel(Math.max(na, nb));
}
